<template>
  <div class="chat-dialog-header">
    <div class="header-brand">
      <div class="header-logo-frame">
        <img v-if="customLogo" class="header-logo" :src="customLogo" alt="" @error="handleLogoError" />
        <AbotLogoIcon v-else class="header-logo" />
      </div>
      <span class="header-title">{{ displayName }}</span>
    </div>

    <button type="button" class="header-close-button" :aria-label="t('common.close')" @click="emit('close')">
      <CloseIcon />
    </button>
  </div>
</template>

<script setup lang="ts">
/**
 * Purpose: Render the floating Widget identity pill and the sole close control.
 * Props: logo may contain a legacy horizontal image; missing or failed images use the bundled
 * Abot mark. The backend default-name sentinel is localized while custom names stay unchanged.
 * Emits: close fires only when the explicit close button is activated.
 */
import { computed, ref, watch } from 'vue'
import { t } from '../../../i18n/simple-i18n'
import AbotLogoIcon from '../../icon/AbotLogoIcon.vue'
import CloseIcon from '../../icon/CloseIcon.vue'

const props = defineProps<{
  logo?: string
  channelName?: string
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const DEFAULT_CHANNEL_NAME_KEY = 'setting.channel.defaultWeb'
const logoLoadFailed = ref(false)
const customLogo = computed(() => (!logoLoadFailed.value ? props.logo?.trim() || '' : ''))
const displayName = computed(() => {
  const channelName = props.channelName?.trim()
  if (!channelName) return 'Abot'

  return channelName === DEFAULT_CHANNEL_NAME_KEY ? t(DEFAULT_CHANNEL_NAME_KEY) : channelName
})

watch(
  () => props.logo,
  () => {
    logoLoadFailed.value = false
  },
)

const handleLogoError = () => {
  logoLoadFailed.value = true
}
</script>

<style lang="less" scoped>
.chat-dialog-header {
  display: flex;
  width: 100%;
  height: 52px;
  align-items: center;
  gap: 8px;
}

.chat-dialog-header {
  height: 52px;
  border: 1px solid var(--abot-brand-border);
  border-radius: 100px;
  background: var(--abot-brand-surface);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  box-sizing: border-box;
}

.header-brand {
  display: flex;
  min-width: 0;
  padding: 8px 12px;
  align-items: center;
  flex: 1 1 0;
  gap: 8px;
}

.header-logo-frame {
  display: flex;
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  background: #fff;
}

.header-logo {
  display: block;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.header-title {
  min-width: 0;
  overflow: hidden;
  color: #5f6260;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-close-button {
  display: flex;
  width: 52px;
  padding: 16px;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  color: #616961;
  border-radius: 100px;
  background: transparent;
  cursor: pointer;
  border: none;
}

.header-close-button:focus-visible {
  outline: 2px solid var(--abot-brand-color);
  outline-offset: 2px;
}
</style>
